import React, {useCallback, useEffect, useState} from 'react';
import createAxiosInstance from '../../support/AxiosSupport';
import Spinner from '../../components/Spinner';

/** Настройки фоновых задач владельца (фича background-jobs-settings). */
export interface BackgroundTaskSettings {
    aiAnalysisEnabled: boolean;
    analyticsEnabled: boolean;
    autopilotEnabled: boolean;
    /** Фоновая очередь выбытия (analytics-deeplinks-bg-disposal, FR-6). */
    disposalEnabled: boolean;
}

type ToggleKey = keyof BackgroundTaskSettings;

/** Описание тумблеров: заголовок + «что перестанет происходить» при выключении. */
const TOGGLES: Array<{key: ToggleKey; title: string; description: string}> = [
    {
        key: 'aiAnalysisEnabled',
        title: 'AI-аналитика вещей (профили и индексация)',
        description: 'Если выключить: вещи не будут автоматически анализироваться AI в фоне '
            + '(профили и эмбеддинги), поставленные в очередь задачи подождут включения. '
            + 'Ручное обновление AI-профиля вещи продолжит работать.',
    },
    {
        key: 'analyticsEnabled',
        title: 'Пересчёт скоров и аналитика гардероба',
        description: 'Если выключить: ночной пересчёт скоров вещей, графа сочетаемости и '
            + 'еженедельные инсайты выполняться не будут; скоры и рекомендации будут устаревать.',
    },
    {
        key: 'autopilotEnabled',
        title: 'Фоновый автопилот (dead-stock)',
        description: 'Если выключить: система перестанет по расписанию готовить и применять '
            + 'решения по залежавшимся вещам. Настроенный уровень автопилота сохранится.',
    },
    {
        key: 'disposalEnabled',
        title: 'Фоновая очередь выбытия',
        description: 'Если выключить: ночной пересчёт кандидатов на выбытие выполняться не '
            + 'будет; очередь выбытия продолжит считаться на каждый запрос страницы.',
    },
];

const SETTINGS_URL = '/profile/settings/background-tasks';

const ProfileBackgroundTasksPage: React.FC = () => {
    const [settings, setSettings] = useState<BackgroundTaskSettings | null>(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        setLoadError(false);
        try {
            const api = await createAxiosInstance();
            const res = await api.get<BackgroundTaskSettings>(SETTINGS_URL);
            setSettings(res.data);
        } catch (e) {
            console.error('Error fetching background task settings:', e);
            setLoadError(true);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const toggle = async (key: ToggleKey) => {
        if (!settings || saving) return;
        const next = {...settings, [key]: !settings[key]};
        setSettings(next); // оптимистично, при ошибке откатываем
        setSaving(true);
        setSaveError(null);
        try {
            const api = await createAxiosInstance();
            await api.put(SETTINGS_URL, next);
        } catch (e) {
            console.error('Error saving background task settings:', e);
            setSettings(settings);
            setSaveError('Не удалось сохранить настройку. Попробуйте ещё раз.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-6">
            <div className="card p-6">
                <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Фоновые задачи</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 max-w-xl">
                    Управляйте фоновыми AI- и аналитическими процессами над вашим гардеробом.
                    Тумблеры действуют на сервере; ручные действия (распознавание фото, обновление
                    AI-профиля вещи) они не блокируют.
                </p>

                {loading && (
                    <div className="flex justify-center py-10"><Spinner/></div>
                )}

                {!loading && loadError && (
                    <div className="mt-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4 text-sm text-red-700 dark:text-red-300 flex items-center justify-between gap-3">
                        <span>Не удалось загрузить настройки фоновых задач.</span>
                        <button className="btn-secondary" onClick={load}>Повторить</button>
                    </div>
                )}

                {!loading && !loadError && settings && (
                    <div className="mt-4 divide-y divide-gray-100 dark:divide-gray-700/50">
                        {TOGGLES.map(({key, title, description}) => {
                            const enabled = settings[key];
                            return (
                                <div key={key} className="flex items-start justify-between gap-4 py-4 min-h-[44px]">
                                    <div>
                                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{title}</p>
                                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 max-w-lg">{description}</p>
                                    </div>
                                    <button
                                        type="button"
                                        role="switch"
                                        aria-checked={enabled}
                                        aria-label={title}
                                        disabled={saving}
                                        onClick={() => toggle(key)}
                                        className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-60 ${
                                            enabled ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-600'
                                        }`}
                                    >
                                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${enabled ? 'translate-x-6' : 'translate-x-1'}`}/>
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                )}

                {saveError && (
                    <div className="mt-2 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-3 text-sm text-red-700 dark:text-red-300">
                        {saveError}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ProfileBackgroundTasksPage;
